import { formatCurrencyAmount } from "@/client/money";
import type { OperationStatus, RefundOperation } from "@/shared/types";

type OperationsDialogProps = {
  open: boolean;
  loading: boolean;
  error?: string;
  operations: RefundOperation[];
  currentAccountId?: string;
  onClose: () => void;
  onResume: (operation: RefundOperation) => void;
};

type StatusPresentation = {
  label: string;
  description: string;
  tone: "success" | "progress" | "warning";
};

const statusPresentation: Record<OperationStatus, StatusPresentation> = {
  draft: {
    label: "Vorbereitet",
    description: "Die Rückgabe wurde angelegt, aber noch nicht gebucht.",
    tone: "progress",
  },
  items_booked: {
    label: "Pfand gebucht",
    description: "Die Pfandartikel wurden auf dem Bon erfasst.",
    tone: "progress",
  },
  awaiting_checkout: {
    label: "Checkout ausstehend",
    description: "Der verbleibende Bon muss noch abgeschlossen werden.",
    tone: "progress",
  },
  refund_pending: {
    label: "Auszahlung ausstehend",
    description: "Die Auszahlung kann sicher fortgesetzt werden.",
    tone: "progress",
  },
  accepted: {
    label: "Vom Terminal angenommen",
    description: "Die endgültige Abrechnung wird noch bestätigt.",
    tone: "progress",
  },
  adyen_succeeded: {
    label: "Auszahlung bestätigt",
    description: "Der Kassenabschluss steht noch aus.",
    tone: "progress",
  },
  pos_finalizing: {
    label: "Kassenabschluss ungeklärt",
    description: "Lightspeed und Adyen müssen manuell abgeglichen werden.",
    tone: "warning",
  },
  pos_finalization_failed: {
    label: "Kassenabschluss fehlgeschlagen",
    description: "Lightspeed und Adyen müssen manuell abgeglichen werden.",
    tone: "warning",
  },
  completed: {
    label: "Abgeschlossen",
    description: "Auszahlung und Kassenabschluss sind abgeschlossen.",
    tone: "success",
  },
  blocked: {
    label: "Blockiert",
    description:
      "Der Vorgang wurde zum Schutz vor einer Doppelzahlung gestoppt.",
    tone: "warning",
  },
  failed: {
    label: "Fehlgeschlagen",
    description: "Der Vorgang konnte nicht abgeschlossen werden.",
    tone: "warning",
  },
  needs_reconciliation: {
    label: "Manueller Abgleich nötig",
    description: "Lightspeed und Adyen müssen manuell abgeglichen werden.",
    tone: "warning",
  },
};

const terminalStatuses = new Set<OperationStatus>([
  "completed",
  "blocked",
  "failed",
]);

const manualStatuses = new Set<OperationStatus>([
  "pos_finalizing",
  "pos_finalization_failed",
  "needs_reconciliation",
]);

export function OperationsDialog({
  open,
  loading,
  error,
  operations,
  currentAccountId,
  onClose,
  onResume,
}: OperationsDialogProps) {
  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        aria-labelledby="operations-title"
        aria-modal="true"
        className="operations-dialog"
        role="dialog"
      >
        <header className="dialog-header">
          <button className="dialog-close" type="button" onClick={onClose}>
            Schließen
          </button>
          <h2 id="operations-title">Vorgänge</h2>
        </header>

        <div className="operations-content">
          <div className="operations-intro">
            <h3>Verlauf der Pfandrückgaben</h3>
            <p>
              Die letzten 50 Vorgänge dieser Kasse. Unterbrochene Vorgänge des
              aktuellen Bons können hier sicher fortgesetzt werden.
            </p>
          </div>

          {loading ? (
            <p className="operations-empty">Vorgänge werden geladen …</p>
          ) : error ? (
            <p className="operations-error">{error}</p>
          ) : operations.length === 0 ? (
            <p className="operations-empty">Noch keine Vorgänge vorhanden.</p>
          ) : (
            <ol className="operations-list">
              {operations.map((operation) => {
                const presentation = statusPresentation[operation.status];
                const currentOpen =
                  operation.accountId === currentAccountId &&
                  !terminalStatuses.has(operation.status);
                const quantity = operation.requestedItems.reduce(
                  (total, item) => total + item.quantity,
                  0,
                );
                const amount =
                  operation.refundAmountMinor ??
                  operation.expectedBalanceDeltaMinor;

                return (
                  <li
                    className={`operation-entry ${currentOpen ? "current" : ""}`}
                    key={operation.id}
                  >
                    <div className="operation-topline">
                      <div>
                        <time dateTime={operation.createdAt}>
                          {formatOperationDate(operation.createdAt)}
                        </time>
                        {currentOpen ? (
                          <span className="current-operation-label">
                            Aktueller Bon
                          </span>
                        ) : null}
                      </div>
                      <span className={`operation-status ${presentation.tone}`}>
                        {presentation.label}
                      </span>
                    </div>

                    <div className="operation-summary">
                      <strong>
                        {formatCurrencyAmount(amount, operation.currency)}
                      </strong>
                      <span>{quantity} Artikel</span>
                    </div>

                    <p>{presentation.description}</p>
                    {operation.failureReason ? (
                      <p className="operation-failure">
                        {operation.failureReason}
                      </p>
                    ) : null}

                    <div className="operation-meta">
                      <span title={operation.accountId}>
                        Bon {shortIdentifier(operation.accountId)}
                      </span>
                      <span title={operation.id}>
                        Vorgang {shortIdentifier(operation.id)}
                      </span>
                    </div>

                    {currentOpen ? (
                      <button
                        className="button button-secondary operation-action"
                        type="button"
                        onClick={() => onResume(operation)}
                      >
                        {manualStatuses.has(operation.status)
                          ? "Hinweis anzeigen"
                          : "Sicher fortsetzen"}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}

function formatOperationDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function shortIdentifier(value: string) {
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}
